<!-- Enhanced Incoming Parcels Component with Team-Based Permissions -->
<script setup>
definePage({
  meta: {
    requiresAuth: true,
    action: "read",
    subject: "Parcels", // Changed from IncomingParcels to Parcels to match permissions
    title: "Incoming Parcels",
  },
})

import { computed, onMounted, reactive, ref, watch, nextTick } from "vue"
import { useIncomingParcelsManagement } from "@/composables/useIncomingParcelsManagement"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useSnackbar } from "@/composables/useSnackbar"
import { useCookie } from "@core/composable/useCookie"
import { getStatusColor } from "@/utils/statusUtils"
import axios from "axios"

// Use enhanced CASL composable
import { useAbility } from "@/plugins/casl/composables/useAbility"
import { useSuperadmin } from "@/composables/useSuperadmin"

// Import ParcelViewModal component
import ParcelViewModal from "@/components/shipment/ParcelViewModal.vue"
import CustomerSelector from "@/components/shipment/parcel/CustomerSelector.vue"
import ProductManager from "@/components/shipment/parcel/ProductManager.vue"
import ContactForm from "@/components/ContactForm.vue"
import { useParcelStore } from "@/@core/stores/parcelStore"
import SingleImageBarcodeUpload from "@/components/shipment/parcel/SingleImageBarcodeUpload.vue"

const { t, locale } = useI18n()
const router = useRouter()
const { success, error } = useSnackbar()

const { parcels, parcelsMeta, fetchParcels, loading } =
  useIncomingParcelsManagement()

const parcelStore = useParcelStore()

// Simple permission checking
const { can, isAdmin, getTeamBadgeColor } = useAbility()
const { isSuperadmin } = useSuperadmin()

// Permission checks based on simplified system
const canEditParcel = computed(() => {
  return can("edit", "Parcels") || isAdmin.value
})

// Delete is hard-locked to superadmin only — matches the
// RestrictDeletesToSuperadmin backend middleware. Even admin gets a 403
// from the backend, so don't even show the button to them.
const canDeleteParcel = computed(() => {
  return isSuperadmin.value
})

const canCreateParcel = computed(() => {
  return can("create", "Parcels") || isAdmin.value
})

const canBulkUpdate = computed(() => {
  return can("edit", "Parcels") || isAdmin.value
})

// Import Excel dialog state
const importDialog = ref(false)

// State
const selectedRows = ref([])

// Claim / reassign dialog
const claimDialogOpen = ref(false)
const claimDialogMode = ref("single") // 'single' | 'bulk'
const claimDialogParcel = ref(null)   // single mode
const claimDialogParcelIds = ref([])  // bulk mode

const openClaimDialog = parcel => {
  claimDialogMode.value = "single"
  claimDialogParcel.value = parcel
  claimDialogParcelIds.value = []
  claimDialogOpen.value = true
}

const openBulkClaimDialog = () => {
  if (!selectedRows.value.length) return
  claimDialogMode.value = "bulk"
  claimDialogParcel.value = null
  claimDialogParcelIds.value = selectedRows.value.map(r => r.id ?? r)
  claimDialogOpen.value = true
}

const onClaimDone = () => {
  // Refresh the list — attached / reassigned rows now show the right
  // customer + may have status_bumped (pending → received).
  loadParcels?.()
  selectedRows.value = []
}

const selectedTeam = ref(null)
const teamOptions = ref([])
const monthOptions = ref([])
const importLoading = ref(false)
const importErrorMessage = ref("")
const importProgressMessage = ref("")
const importSuccessMessage = ref("")
const containers = ref([])
const importId = ref(null)
const importProgress = ref(0)
const importIntervalId = ref(null)
const loadingContainers = ref(false)
const showFileButton = ref(false)
const isLoading = ref(false)

const showBulkEditDialog = ref(false)
const showConfirmDeleteDialog = ref(false)
const importedFileDialog = ref(false)
const showSuccessAlert = ref(false)
const showSingleImageScanDialog = ref(false)

// Enhanced progress tracking
const bulkImportProgress = reactive({
  current: 0,
  total: 0,
  percentage: 0,
  successCount: 0,
  errorCount: 0,
  currentItem: "",
  errors: [],
  cancelled: false,
})

// const fileData = ref([]);

const importForm = ref({
  excel_file: null,
})

const form = ref({})

// Handle Excel file upload
const uploadExcelFile = async () => {
  if (!importForm.value.excel_file) {
    error("Please fill all required fields")

    return
  }

  importLoading.value = true

  // importProgress.value = 40;
  importProgressMessage.value = "Uploading file..."
  importErrorMessage.value = ""
  importSuccessMessage.value = ""

  try {
    console.log("-- invoking uploadExcelFile --")

    // Create form data
    const formData = new FormData()

    formData.append("excel_file", importForm.value.excel_file)
    console.log("form data ==>", importForm.value.excel_file.name)

    // Upload the file
    const response = await axios.post(
      "/shipment/incoming-parcels/import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )

    console.log("response  =>", response)

    if (response.data.success) {
      importId.value = response.data.import_id
      importSuccessMessage.value = "Import started successfully"

      // importProgress.value = 70;
      importProgressMessage.value = "Processing file..."
      console.log("importId :", importId.value)

      // Start polling for progress updates
      startProgressPolling()
    } else {
      importErrorMessage.value = response.data.message || "Import failed"
      importLoading.value = false
      error(response.data.message || "Import failed")
    }
  } catch (err) {
    console.error("Error uploading file:", err)
    importErrorMessage.value =
      err.response?.data?.message || "Failed to upload file"
    importLoading.value = false
    error("Failed to upload file")
  }
}

// Fetch container arrivals for import form
const fetchContainerArrivalsForImport = async () => {
  loadingContainers.value = true
  try {
    const response = await axios.get("shipment/container-arrivals", {
      params: {
        per_page: -1, // Get all containers
      },
    })

    containers.value = response.data.data || []
  } catch (err) {
    console.error("Error fetching containers:", err)
    error("Failed to fetch containers")
  } finally {
    loadingContainers.value = false
  }
}

// Poll for import progress updates
const startProgressPolling = () => {
  // Clear any existing interval
  if (importIntervalId.value) {
    clearInterval(importIntervalId.value)
  }

  // Start new polling interval
  importIntervalId.value = setInterval(async () => {
    try {
      console.log("-- invoking startProgressPolling --")

      const response = await axios.get(
        `shipment/incoming-parcels/import-progress/${importId.value}`,
      )

      console.log("Polling response ==>", response.data)

      if (response.data.success) {
        const progressData = response.data.data

        parcelStore.addIncomingParcel(progressData.rows)

        // showFileButton.value = true;
        importedFileDialog.value = true
        importProgress.value = progressData.progress || 0
        importProgressMessage.value = progressData.message

        // Check if import is complete
        if (
          progressData.status === "completed" ||
          progressData.progress >= 100
        ) {
          stopProgressPolling()

          if (progressData.status === "completed") {
            importSuccessMessage.value = "Import completed successfully"
            success("Import completed successfully")
            fetchParcels()
            setTimeout(() => {
              importDialog.value = false
              resetImportForm()
            }, 2000)
          } else {
            importErrorMessage.value = progressData.message || "Import failed"
            error(progressData.message || "Import failed")
          }

          importLoading.value = false
        }
      } else {
        stopProgressPolling()
        importErrorMessage.value = "Failed to fetch import progress"
        importLoading.value = false
      }
    } catch (err) {
      console.error("Error checking import progress:", err)
    }
  }, 2000) // Poll every 2 seconds
}

// Stop polling for progress updates
const stopProgressPolling = () => {
  if (importIntervalId.value) {
    clearInterval(importIntervalId.value)
    importIntervalId.value = null
  }
}

// Reset import form and state
const resetImportForm = () => {
  importForm.value = {
    container_arrival_id: null,
    excel_file: null,
  }
  importProgress.value = 0
  importProgressMessage.value = ""
  importSuccessMessage.value = ""
  importErrorMessage.value = ""
  importId.value = null
  importLoading.value = false
}

// Watch for dialog close to reset form
watch(importDialog, newVal => {
  if (newVal) {
    // Fetch containers when dialog opens
    fetchContainerArrivalsForImport()
  } else {
    // Reset form and stop polling when dialog closes
    stopProgressPolling()
    resetImportForm()
  }
})

// Modal state for parcel viewing
const viewDialogOpen = ref(false)
const selectedParcelForView = ref(null)
const showUploadImagesDialog = ref(false)

// Upload state
const uploadedImages = ref([])
const uploadingImages = ref(false)

// Bulk edit state
const bulkEditForm = ref({
  customer_id: null,
  notes: "",
  parcel_type: "",
  transport: null,
  team_id: null,
  commission_agent: null,
  products: [],
  length: null,
  width: null,
  height: null,
  weight: null,
  volume: null,
  bag_code: "",
  replace_products: true,
})

const bulkEditLoading = ref(false)
const bulkEditActiveTab = ref(0)
const bulkEditCustomerSelection = ref(null)
const showBulkCustomerModal = ref(false)
const newBulkCustomerName = ref("")

// Pagination state
const currentPage = ref(1)
const itemsPerPage = ref(10)
const isRestoringState = ref(false)
const tableKey = ref(0)

// Guard against the duplicate-fetch race on page load. VDataTableServer
// emits @update:options once when it mounts (with default page=1,
// itemsPerPage=10), and onMounted ALSO calls loadParcels() once it has
// restored session state and fetched filter options. Without this guard
// both fire back-to-back at ~600ms apart, hitting the same query twice.
//
// Flow: onMounted sets pageReady=true after its own loadParcels() resolves.
// Table updates that happen before that (the initial sync emission) are
// ignored. Real user-driven pagination/sort changes pass through normally.
const pageReady = ref(false)

// Two-way bound to VDataTableServer's sort-by — keeps the visible header
// arrow in sync with what we actually request. The backend honors the
// sort_by/sort_order params (see IncomingParcelController switch on
// barcode/bag_code/customer/date/status). Empty array = no user-driven
// sort, table goes by the controller's default (latest first).
const tableSort = ref([])

// Filters
const filters = reactive({
  status: "",
  team_id: null,
  transport: "all",
  month: null,
  search: "",
})

// Table Imported File Headres
// Headers read NORMALIZED keys emitted by the parse-only job
// (IncomingParcelExtractionImportJob). The job auto-detects each Chinese/
// French header label and maps it to a canonical key (_barcode, _bag_code,
// _customer_name, etc.) so the table is correct regardless of which
// template China used. Falls back to the legacy raw column letters
// when normalized values are missing (older imports / unknown templates).
const fileTableHeaders = [
  { title: "Serial number", key: "_serial_number" },
  { title: "Customer", key: "_customer_name" },
  { title: "Phone", key: "_customer_phone" },
  { title: "Bag Code", key: "_bag_code" },
  { title: "Barcode", key: "_barcode" },
  { title: "Product", key: "_product_name" },
  { title: "Quantity", key: "_quantity" },
  { title: "Weight", key: "_weight" },
  { title: "Long", key: "_length" },
  { title: "Width", key: "_width" },
  { title: "High", key: "_height" },
]

// Table headers aligned with outgoing packages order
const tableHeaders = computed(() => [
  {
    title: t("tableHeaders.image"),
    key: "image",
    align: "center",
    sortable: false,
  },
  {
    title: t("tableHeaders.barcode"),
    key: "barcode",
    align: "start",
    sortable: true,
  },
  {
    title: t("tableHeaders.bag_code") || "Bag Code",
    key: "bag_code",
    align: "start",
    sortable: true,
  },
  {
    title: t("tableHeaders.date"),
    key: "date",
    align: "center",
    sortable: true,
  },
  {
    title: t("tableHeaders.status"),
    key: "status",
    align: "center",
    sortable: true,
  },
  {
    title: t("tableHeaders.customer"),
    key: "customer",
    align: "start",
    sortable: true,
  }, // Using 'parcels' label but 'customer' key for backward compatibility
  {
    title: t("tableHeaders.products"),
    key: "products",
    align: "start",
    sortable: false,
  },
  { title: t("shipment.team"), key: "team", align: "center", sortable: false },
  {
    title: t("tableHeaders.transport"),
    key: "transport",
    align: "center",
    sortable: true,
  },
  {
    title: t("tableHeaders.weight"),
    key: "weight",
    align: "end",
    sortable: true,
  },
  {
    title: t("tableHeaders.volume"),
    key: "volume",
    align: "end",
    sortable: true,
  },
  {
    title: t("tableHeaders.actions"),
    key: "actions",
    align: "center",
    sortable: false,
  },
])

// Canonical 7 customer-visible parcel statuses + counter_verified (internal).
// Source of truth: vuexy-godana ParcelStatus + godana-chatbot + lunar-store.
const statusOptions = computed(() => [
  { title: t("shipment.statuses.pending"), value: "pending" },
  { title: t("shipment.statuses.received"), value: "received" },
  { title: t("shipment.statuses.packaged"), value: "packaged" },
  { title: t("shipment.statuses.shipped"), value: "shipped" },
  { title: t("shipment.statuses.arrived"), value: "arrived" },
  { title: t("shipment.statuses.counter_verified"), value: "counter_verified" },
  { title: t("shipment.statuses.ready_for_pickup"), value: "ready_for_pickup" },
  { title: t("shipment.statuses.delivered"), value: "delivered" },
])

// Check if user can access specific parcel
const canAccessParcel = parcel => {
  if (isAdmin.value) return true

  // With simplified permissions, anyone with read permission can access parcels
  return can("read", "Parcels")
}

// Parcel-specific permissions
const canEditSpecificParcel = parcel => {
  return canEditParcel.value
}

const canDeleteSpecificParcel = parcel => {
  return canDeleteParcel.value
}

// Normalize status for consistent translation keys
const normalizeStatus = status => {
  return status.toLowerCase().replace(/\s+/g, "_").trim()
}

// Now using shared getStatusColor function from statusUtils.js

const formatMonthLabel = monthValue => {
  if (!monthValue) return ""

  const date = new Date(`${monthValue}-01T00:00:00`)

  if (Number.isNaN(date.getTime())) {
    return monthValue
  }

  return new Intl.DateTimeFormat(locale.value || "en", {
    month: "long",
    year: "numeric",
  }).format(date)
}

const generateDefaultMonthOptions = (count = 12) => {
  const options = []
  const now = new Date()

  for (let i = 0; i < count; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)

    const value = `${date.getFullYear()}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}`

    options.push({
      title: formatMonthLabel(value),
      value,
    })
  }

  return options
}

const buildMonthOptions = monthsFromApi => {
  if (Array.isArray(monthsFromApi) && monthsFromApi.length) {
    return monthsFromApi.map(monthValue => ({
      title: formatMonthLabel(monthValue),
      value: monthValue,
    }))
  }

  return generateDefaultMonthOptions()
}

const getMonthDateRange = monthValue => {
  if (!monthValue || typeof monthValue !== "string") return null

  const [year, month] = monthValue.split("-").map(part => parseInt(part, 10))

  if (!year || !month || month < 1 || month > 12) return null

  const startDateObj = new Date(year, month - 1, 1)
  const endDateObj = new Date(year, month, 0)

  const formatDate = date => date.toISOString().split("T")[0]

  return {
    start: formatDate(startDateObj),
    end: formatDate(endDateObj),
  }
}

// Fetch filter options
const fetchFilterOptions = async () => {
  try {
    const response = await axios.get(
      "/shipment/incoming-parcels/filter-options",
    )

    if (response.data.teams) {
      teamOptions.value = response.data.teams
    }

    monthOptions.value = buildMonthOptions(response.data.months)
  } catch (err) {
    console.error("Failed to fetch filter options:", err)
  }

  // Ensure we always have something to show in the month select
  if (!monthOptions.value.length) {
    monthOptions.value = generateDefaultMonthOptions()
  }
}

// Load parcels
const loadParcels = async () => {
  const params = { ...filters }

  const monthRange = getMonthDateRange(filters.month)

  if (monthRange) {
    params.start_date = monthRange.start
    params.end_date = monthRange.end
  }

  // Month is transformed into start_date/end_date for the API
  delete params.month

  Object.keys(params).forEach(key => {
    if (params[key] === "" || params[key] === null || params[key] === "all") {
      delete params[key]
    }
  })

  // Add pagination parameters
  params.page = currentPage.value
  params.per_page = itemsPerPage.value

  await fetchParcels(params)
}

// Watch filters - reset to page 1 when filters change
watch(
  () => [
    filters.status,
    filters.team_id,
    filters.transport,
    filters.month,
    filters.search,
  ],
  () => {
    // Don't reset page if we're restoring state from sessionStorage
    if (!isRestoringState.value) {
      currentPage.value = 1
      loadParcels()
    }
  },
  { deep: true },
)

// Refresh month labels when locale changes
watch(
  () => locale.value,
  () => {
    if (!monthOptions.value.length) return

    monthOptions.value = monthOptions.value.map(option => ({
      value: option.value,
      title: formatMonthLabel(option.value),
    }))
  },
)

// Initialize
onMounted(async () => {
  // Check if user can read incoming parcels or is admin
  if (!can("read", "Parcels") && !isAdmin.value) {
    error("No permission to view incoming parcels")
    router.push("/unauthorized")

    return
  }

  await fetchFilterOptions()

  // Restore state if coming back from edit page
  const savedState = sessionStorage.getItem("incomingParcelsState")

  console.log("Checking for saved state:", savedState)
  if (savedState) {
    try {
      isRestoringState.value = true

      const state = JSON.parse(savedState)

      console.log("Restoring state:", state)
      console.log(
        "Before restore - page:",
        currentPage.value,
        "itemsPerPage:",
        itemsPerPage.value,
      )
      currentPage.value = state.page || 1
      itemsPerPage.value = state.itemsPerPage || 10
      if (state.filters) {
        Object.assign(filters, state.filters)
      }
      console.log(
        "After restore - page:",
        currentPage.value,
        "itemsPerPage:",
        itemsPerPage.value,
      )

      // Clear the saved state
      sessionStorage.removeItem("incomingParcelsState")

      // Force table re-render by changing key
      tableKey.value++

      // Wait for Vue to update the DOM before loading parcels
      await nextTick()
      isRestoringState.value = false
    } catch (e) {
      console.error("Failed to restore state:", e)
      isRestoringState.value = false
    }
  } else {
    console.log("No saved state found")
  }

  await loadParcels()

  // Now that the initial fetch has fired, let the table emissions through.
  // Any user-driven page/sort change after this point goes through
  // handleTableOptionsUpdate normally. Wait one tick so a stale emission
  // queued by the table during loadParcels() can't sneak in.
  await nextTick()
  pageReady.value = true
})

// Helper function to format date according to locale
const formatDate = dateString => {
  if (!dateString) return "-"

  try {
    const date = new Date(dateString)
    const currentLocale = locale.value || "en"

    // Use Intl.DateTimeFormat with the current i18n locale for consistent formatting
    return new Intl.DateTimeFormat(currentLocale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  } catch (e) {
    // Return the original string if there's an error
    return dateString
  }
}

// Helper function to get image URL
const getImageUrl = item => {
  if (
    !item ||
    !item.images ||
    !item.images.length ||
    !item.images[0] ||
    !item.images[0].image_path
  ) {
    return "/img/default.png"
  }

  const imagePath = item.images[0].image_path

  // Ensure path starts with a slash
  return imagePath.startsWith("/") ? imagePath : "/" + imagePath
}

// Handle image click based on whether the parcel has images
const handleImageClick = parcel => {
  // Close any open dialogs first
  viewDialogOpen.value = false
  showUploadImagesDialog.value = false

  // Create a copy of parcel with belongs_to mapped from team for modal compatibility
  const parcelForModal = {
    ...parcel,
    belongs_to: parcel.team?.code
      ? mapTeamToBelongsTo(parcel.team.code)
      : parcel.belongs_to ?? 1,
  }

  // Set the selected parcel
  selectedParcelForView.value = parcelForModal

  // Use a small delay to ensure dialogs are fully closed before opening a new one
  setTimeout(() => {
    if (parcel.images && parcel.images.length > 0) {
      // If the parcel has images, show the view dialog
      viewDialogOpen.value = true
    } else {
      // If the parcel has no images, show the upload dialog
      showUploadImagesDialog.value = true
    }
  }, 50)
}

// Helper function to map team to belongs_to value
const mapTeamToBelongsTo = teamCode => {
  const teamMap = {
    GB: 0,
    GD: 1,
    JP: 2,
  }

  return teamMap[teamCode] ?? 1 // Default to GD (1) if not found
}

// Pagination handlers
const handlePageChange = page => {
  currentPage.value = page
  loadParcels()
}

const handleItemsPerPageChange = perPage => {
  itemsPerPage.value = perPage
  currentPage.value = 1 // Reset to first page when changing items per page
  loadParcels()
}

// Handle VDataTableServer options update
const handleTableOptionsUpdate = options => {
  // Skip the table's initial @update:options emission on mount — onMounted
  // is in charge of the first fetch (after session-state restore + filter
  // options are loaded). Without this guard the same /incoming-parcels query
  // fires twice on page load.
  if (!pageReady.value) return

  if (options.page !== currentPage.value) {
    currentPage.value = options.page
  }
  if (options.itemsPerPage !== itemsPerPage.value) {
    itemsPerPage.value = options.itemsPerPage
    currentPage.value = 1 // Reset to first page when changing items per page
  }

  // Handle sorting
  if (options.sortBy && options.sortBy.length > 0) {
    const sortItem = options.sortBy[0]

    filters.sort_by = sortItem.key
    filters.sort_order = sortItem.order || "asc"
  } else {
    // Clear sorting when no sort is active
    delete filters.sort_by
    delete filters.sort_order
  }

  loadParcels()
}

// Actions
const viewParcel = parcel => {
  // Create a copy of parcel with belongs_to mapped from team for modal compatibility
  const parcelForModal = {
    ...parcel,
    belongs_to: parcel.team?.code
      ? mapTeamToBelongsTo(parcel.team.code)
      : parcel.belongs_to ?? 1,
  }

  selectedParcelForView.value = parcelForModal
  viewDialogOpen.value = true
}

const editParcel = parcel => {
  // Store current state before navigating
  const stateToSave = {
    page: currentPage.value,
    itemsPerPage: itemsPerPage.value,
    filters: filters,
  }

  console.log("Saving state before edit:", stateToSave)
  sessionStorage.setItem("incomingParcelsState", JSON.stringify(stateToSave))
  router.push(`/shipment/incoming-parcels/${parcel.id}/edit`)
}

// State for package creation
const showPackageTypeDialog = ref(false)
const packageMode = ref("individual")

// Confirm delete selected rows
const confirmDeleteSelected = () => {
  if (!selectedRows.value.length) return
  showConfirmDeleteDialog.value = true
}

// Delete Selected Rows
const handleDeleteSelected = async () => {
  if (!selectedRows.value.length) return

  try {
    const token = useCookie("accessToken").value
    const parcelIds = selectedRows.value.map(row => row.id || row)

    const response = await axios.post(
      "/shipment/incoming-parcels/mass-destroy",
      {
        selected_rows: parcelIds.join(","),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    )

    if (response.data.success) {
      showConfirmDeleteDialog.value = false
      selectedRows.value = []
      await loadParcels()
      success(t("messages.parcelsDeleted", "Parcels deleted successfully"))
    }
  } catch (err) {
    console.error("Error in handleDeleteSelected:", err)
    error(t("messages.deleteError", "Error deleting parcels"))
  }
}

// Create package from selected parcels
const createPackageFromSelected = async () => {
  if (!selectedRows.value.length) return

  try {
    const token = useCookie("accessToken").value
    const parcelIds = selectedRows.value.map(row => row.id || row)

    // First check if all selected parcels have status 'received'
    const { data: parcelsResponse } = await axios.get(
      `/shipment/incoming-parcels/by-ids?ids=${parcelIds.join(
        ",",
      )}&include_translations=true`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    )

    // Check if any parcel has status other than 'received'
    const invalidParcels = []
    if (parcelsResponse && parcelsResponse.data) {
      parcelsResponse.data.forEach(parcel => {
        if (parcel.status_key !== "received") {
          invalidParcels.push({
            id: parcel.id,
            barcode: parcel.barcode || parcel.id,
            status: parcel.status,
            status_key: parcel.status_key,
          })
        }
      })
    }

    // If there are invalid parcels, show error message and return
    if (invalidParcels.length > 0) {
      const invalidParcelMessage =
        invalidParcels.length === 1
          ? `Parcel ${invalidParcels[0].barcode} has status "${
            invalidParcels[0].status_key || invalidParcels[0].status
          }". Only parcels with status "received" can be packaged.`
          : `${invalidParcels.length} parcels have invalid status. Only parcels with status "received" can be packaged.`

      error(invalidParcelMessage)

      return
    }

    // If only one parcel is selected, redirect to create form with parcel_id
    if (parcelIds.length === 1) {
      router.push({
        path: "/shipment/outgoing-packages/create",
        query: { parcel_id: parcelIds[0] },
      })
    } else {
      // For multiple parcels, show dialog to ask for creation mode
      showPackageTypeDialog.value = true
    }
  } catch (err) {
    console.error("Package creation error:", err.response?.data || err)
    error(t("messages.packageError", "Error creating package"))
  }
}

// Handle package type selection
const handlePackageTypeSelection = async selectedMode => {
  try {
    const token = useCookie("accessToken").value

    showPackageTypeDialog.value = false

    const parcelIds = selectedRows.value.map(row => row.id || row)

    if (selectedMode === "single") {
      // Create one package with all selected parcels
      const response = await axios.post(
        "/shipment/incoming-parcels/create-package",
        {
          selected_rows: parcelIds.join(","),
          mode: "single",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )

      if (response.data.success) {
        router.push({
          path: "/shipment/outgoing-packages/create",
          query: { parcel_ids: parcelIds.join(",") },
        })
      }
    } else {
      // Create individual packages
      const response = await axios.post(
        "/shipment/incoming-parcels/create-individual-packages",
        {
          selected_rows: parcelIds.join(","),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )

      if (response.data.success) {
        success(
          t(
            "messages.individualPackagesCreated",
            "Individual packages created successfully",
          ),
        )
        router.push("/shipment/outgoing-packages")
      }
    }
  } catch (err) {
    console.error("Package creation error:", err.response?.data || err)
    error(t("messages.packageError", "Error creating package"))
  }
}

const handleBulkUpdate = async () => {
  if (!canBulkUpdate.value) {
    error("No permission")

    return
  }
  showBulkEditDialog.value = true
}

const handleMassDelete = async () => {
  if (!canDeleteParcel.value) {
    error("No permission")

    return
  }
  confirmDeleteSelected()
}

// Helper function to create image URL
const createImageURL = file => {
  try {
    return window.URL?.createObjectURL(file) || "/img/default.png"
  } catch (error) {
    console.error("Error creating image URL:", error)

    return "/img/default.png"
  }
}

// Upload functions
const handleImageUpload = event => {
  const files = event.target.files
  if (!files || files.length === 0) return

  // Convert FileList to array and store
  uploadedImages.value = Array.from(files)
}

// Handle parcel updated event from modal
const handleParcelUpdated = async updatedParcel => {
  // Refresh the parcel list to show updated data
  await loadParcels()

  // Show success message if not already shown
  success(t("messages.parcelUpdated", "Parcel updated successfully"))
}

const uploadImages = async () => {
  if (
    !uploadedImages.value.length ||
    !selectedParcelForView.value ||
    !selectedParcelForView.value.id
  ) {
    error(t("messages.missingParcelOrImages", "Missing parcel ID or images"))

    return
  }

  uploadingImages.value = true

  try {
    const formData = new FormData()

    formData.append("parcel_id", selectedParcelForView.value.id)

    // Append each image to form data
    uploadedImages.value.forEach(image => {
      formData.append("images[]", image)
    })

    // Get the auth token
    const token = document.cookie
      .split("; ")
      .find(row => row.startsWith("accessToken="))
      ?.split("=")[1]

    // Upload images using the API endpoint with authentication
    const response = await axios.post(
      "/shipment/incoming-parcels/upload-images",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      },
    )

    if (response.data.success) {
      // Reset the form and close the dialog
      uploadedImages.value = []
      showUploadImagesDialog.value = false

      // Refresh the data
      await loadParcels()

      // Show success notification
      success(t("messages.imagesUploaded", "Images uploaded successfully"))
    } else {
      error(
        response.data.message ||
          t("messages.uploadError", "Failed to upload images"),
      )
    }
  } catch (err) {
    console.error("Upload error:", err.response || err)
    error(
      err.response?.data?.message ||
        t("messages.uploadError", "Failed to upload images"),
    )
  } finally {
    uploadingImages.value = false
  }
}

// Bulk edit functions
const openBulkEditDialog = async () => {
  if (!selectedRows.value.length) return

  // Get the most common values from selected parcels to pre-populate the form
  const getDefaultValue = field => {
    const values = selectedRows.value
      .map(row => row[field])
      .filter(val => val !== null && val !== undefined)

    if (values.length === 0) return null

    // Find the most frequent value
    const counts = {}

    values.forEach(val => {
      counts[val] = (counts[val] || 0) + 1
    })

    return Object.keys(counts).reduce((a, b) =>
      counts[a] > counts[b] ? a : b,
    )
  }

  // Helper function to map team data to team_id
  const getTeamId = parcel => {
    if (parcel.team?.id) return parcel.team.id
    if (parcel.team_id) return parcel.team_id

    return null
  }

  // Pre-populate form with most common values from selected parcels
  bulkEditForm.value = {
    customer_id: null, // Customer selection will be handled separately
    notes: "",
    parcel_type: getDefaultValue("parcel_type") || null,
    transport: (() => {
      const transport = getDefaultValue("transport")

      return transport !== null && transport !== undefined
        ? parseInt(transport)
        : null
    })(),
    team_id: (() => {
      const teamIds = selectedRows.value
        .map(row => getTeamId(row))
        .filter(val => val !== null && val !== undefined)

      if (teamIds.length === 0) return null

      // Find the most frequent team_id
      const counts = {}

      teamIds.forEach(val => {
        counts[val] = (counts[val] || 0) + 1
      })

      return Object.keys(counts).reduce((a, b) =>
        counts[a] > counts[b] ? a : b,
      )
    })(),
    commission_agent: null,

    // Add dimension fields
    length: null,
    width: null,
    height: null,
    weight: null,
    volume: null,
    bag_code: "",
    products: [],
    replace_products: true, // Always replace products
  }

  // Reset other state
  bulkEditActiveTab.value = 0
  bulkEditCustomerSelection.value = null

  // Load existing products from selected parcels
  await loadExistingProducts()

  showBulkEditDialog.value = true
}

const closeBulkEditDialog = () => {
  showBulkEditDialog.value = false
}

// Customer handling for bulk edit
const handleBulkCustomerSelect = customer => {
  if (customer) {
    bulkEditCustomerSelection.value = customer
    bulkEditForm.value.customer_id = customer.id
  } else {
    bulkEditCustomerSelection.value = null
    bulkEditForm.value.customer_id = null
  }
}

const handleBulkCustomerCreateNew = customer => {
  newBulkCustomerName.value = customer.name
  showBulkCustomerModal.value = true
}

const handleBulkCustomerCreated = formattedCustomer => {
  showBulkCustomerModal.value = false
  bulkEditForm.value.customer_id = formattedCustomer.id
  bulkEditCustomerSelection.value = formattedCustomer
}

// Load existing products from selected parcels
const loadExistingProducts = async () => {
  try {
    const token = useCookie("accessToken").value
    const parcelIds = selectedRows.value.map(row => row.id || row)

    // Use the already defined i18n instance instead of calling useI18n() again
    const frontendLocale = t.global?.locale?.value || "en" // Fallback to 'en' if undefined
    const backendLocale = mapLocale(frontendLocale)

    const response = await axios.get(
      `/shipment/incoming-parcels/by-ids?ids=${parcelIds.join(
        ",",
      )}&include_products=true&locale=${backendLocale}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    )

    // Collect unique products (not aggregated quantities)
    const productMap = new Map()

    if (response.data.data && Array.isArray(response.data.data)) {
      response.data.data.forEach((parcel, parcelIndex) => {
        if (parcel.products && Array.isArray(parcel.products)) {
          parcel.products.forEach((product, productIndex) => {
            // Use the underlying shipment_product_id for unique product identification
            const productId =
              product.product_id || product.shipment_product_id || product.id

            const productName =
              product.name || product.product_name || product.text

            if (productId && productName) {
              // Use only the shipment_product_id as the key for unique products
              const key = `${productId}`

              if (!productMap.has(key)) {
                // Add unique product with quantity from first occurrence
                const newProduct = {
                  id: product.id, // Keep the line ID for backend compatibility
                  shipment_product_id: productId, // Store the underlying product ID
                  name: productName,
                  quantity: parseInt(product.quantity || 1), // Quantity per parcel (not aggregated)
                  detail: product.detail || "",
                }

                productMap.set(key, newProduct)
              }
            }
          })
        }
      })
    }

    // Convert Map to array
    bulkEditForm.value.products = Array.from(productMap.values())
  } catch (error) {
    console.error("Error loading existing products:", error)

    // Don't show error to user, just proceed with empty products
    bulkEditForm.value.products = []
  }
}

// Function to map frontend locale to backend locale
const mapLocale = frontendLocale => {
  // Map frontend locale codes to backend locale codes
  const localeMap = {
    zh: "ce", // Chinese in frontend is 'zh', but backend uses 'ce'
    cn: "ce", // Alternative Chinese code
    fr: "fr", // French is same in both
    en: "en", // English is same in both
  }

  // Return the mapped locale or the original if no mapping exists
  return localeMap[frontendLocale] || frontendLocale
}

const handleCreateNewProduct = product => {
  // This would open a product creation modal
  // For now, just show a message
  success(
    t(
      "messages.productCreationNotImplemented",
      "Product creation will be implemented",
    ),
  )
}

// Helper function to get parcel display identifier
const getParcelDisplayId = parcelIdOrObject => {
  // If it's just an ID, find the full parcel object
  let parcel = parcelIdOrObject
  if (
    typeof parcelIdOrObject === "number" ||
    (typeof parcelIdOrObject === "string" && !isNaN(parcelIdOrObject))
  ) {
    parcel = parcels.value?.find(p => p.id == parcelIdOrObject)
  }

  if (!parcel) return `#${parcelIdOrObject}`

  if (parcel.barcode) return parcel.barcode
  if (parcel.tracking_number) return parcel.tracking_number
  if (parcel.reference_number) return parcel.reference_number
  if (parcel.id) return `#${parcel.id}`

  return "Unknown"
}

const submitBulkEdit = async () => {
  if (!selectedRows.value.length) return

  bulkEditLoading.value = true

  try {
    const token = useCookie("accessToken").value

    // Extract IDs from selected rows
    const parcelIds = selectedRows.value.map(row => row.id || row)

    // Prepare update data (only include non-empty values)
    const updateData = {
      parcel_ids: parcelIds,
    }

    // Only include fields that have values
    if (bulkEditForm.value.customer_id) {
      updateData.customer_id = bulkEditForm.value.customer_id
      updateData.replace_customer = true // Flag to replace existing customer
    }
    if (bulkEditForm.value.notes) {
      updateData.notes = bulkEditForm.value.notes
    }
    if (bulkEditForm.value.parcel_type) {
      updateData.parcel_type = bulkEditForm.value.parcel_type
    }
    if (
      bulkEditForm.value.transport !== null &&
      bulkEditForm.value.transport !== ""
    ) {
      updateData.transport = bulkEditForm.value.transport
    }
    if (
      bulkEditForm.value.team_id !== null &&
      bulkEditForm.value.team_id !== ""
    ) {
      updateData.team_id = bulkEditForm.value.team_id
    }
    if (bulkEditForm.value.bag_code) {
      updateData.bag_code = bulkEditForm.value.bag_code
    }

    // Add dimension fields if they have values
    if (
      bulkEditForm.value.length !== null &&
      bulkEditForm.value.length !== ""
    ) {
      updateData.length = bulkEditForm.value.length
    }
    if (bulkEditForm.value.width !== null && bulkEditForm.value.width !== "") {
      updateData.width = bulkEditForm.value.width
    }
    if (
      bulkEditForm.value.height !== null &&
      bulkEditForm.value.height !== ""
    ) {
      updateData.height = bulkEditForm.value.height
    }
    if (
      bulkEditForm.value.weight !== null &&
      bulkEditForm.value.weight !== ""
    ) {
      updateData.weight = bulkEditForm.value.weight
    }
    if (
      bulkEditForm.value.volume !== null &&
      bulkEditForm.value.volume !== ""
    ) {
      updateData.volume = bulkEditForm.value.volume
    }

    if (bulkEditForm.value.products && bulkEditForm.value.products.length > 0) {
      updateData.products = bulkEditForm.value.products
      updateData.replace_products = bulkEditForm.value.replace_products // Use checkbox value
    }

    const response = await axios.post(
      "/shipment/incoming-parcels/bulk-update",
      updateData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    )

    if (response.data.success) {
      success(
        t("shipment.parcels_updated_success", { count: parcelIds.length }),
      )

      // Close dialog and clear selection
      showBulkEditDialog.value = false
      selectedRows.value = []

      // Refresh the data
      await loadParcels()
    } else {
      error(
        response.data.message ||
          t("messages.updateError", "Failed to update parcels"),
      )
    }
  } catch (err) {
    console.error("Bulk edit error:", err.response?.data || err)
    error(
      err.response?.data?.message ||
        t("messages.updateError", "Failed to update parcels"),
    )
  } finally {
    bulkEditLoading.value = false
  }
}

const fileData = computed(() => parcelStore.incomigParcel)

const isFileLoading = computed(() => {
  return !fileData.value || fileData.value.length === 0
})

const createOrGetCustomer = async (name, phone) => {
  if (!name || !phone) {
    console.warn("Incomplete Information :", { name, phone })

    return null
  }

  // Normalize the phone
  let normalizedPhone = phone.toString().trim()
  normalizedPhone = normalizedPhone.replace(/[\s\-]/g, "")
  if (normalizedPhone.startsWith("0")) {
    normalizedPhone = "+261" + normalizedPhone.substring(1)
  } else if (normalizedPhone.startsWith("3")) {
    normalizedPhone = "+261" + normalizedPhone
  }

  console.log(`Phone Normalized: ${phone} → ${normalizedPhone}`)

  try {
    const token = localStorage.getItem("accessToken")

    // Step1 - Check if customer exist
    console.log("Check existence .... ")

    const existingResponse = await axios.get("/shipment/customers", {
      params: { q: normalizedPhone },
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })

    const existingCustomers = existingResponse?.data?.data || []

    console.log("existance data ===>", existingCustomers)

    if (existingCustomers.length > 0) {
      const existing = existingCustomers[0]

      console.log(
        `Existing customer found : ${existing.name} (ID: ${existing.id})`,
      )

      return existing.id
    }

    // Step2 - Create Client if Not Exist
    console.log("Non-existent client, creation in progress...")

    const payload = {
      name: name,
      phone: normalizedPhone,
    }

    console.log("Payload sent:", payload)

    const customerResponse = await axios.post("/shipment/customers", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      timeout: 10000,
    })

    const newCustomer = customerResponse?.data?.data
    if (newCustomer?.id) {
      console.log(
        `Client created successfully : ${newCustomer.name} (ID: ${newCustomer.id})`,
      )

      return newCustomer.id
    } else {
      console.error(
        "Unexpected response to customer creation :",
        customerResponse,
      )

      return null
    }
  } catch (error) {
    console.error("Error verifying or creating client:", error)

    return null
  }
}

const calculateVolume = (length, width, height) => {
  // Float Conversion
  const l = parseFloat(length) || 0
  const w = parseFloat(width) || 0
  const h = parseFloat(height) || 0

  // Volume in m³ if the dimensions are in cm
  const volume = (l * w * h) / 1_000_000

  return parseFloat(volume.toFixed(3))
}

// Map team code from Excel (column A) to team_id from database
const getTeamIdFromCode = teamCode => {
  if (!teamCode)
    return teamOptions.value.length > 0 ? teamOptions.value[0].value : null

  // Find team by code in teamOptions (assuming teams have a 'code' or 'title' property)
  const team = teamOptions.value.find(
    t =>
      t.code?.toUpperCase() === teamCode.toUpperCase() ||
      t.title?.toUpperCase() === teamCode.toUpperCase(),
  )

  if (team) {
    console.log(`Team code '${teamCode}' mapped to team_id: ${team.value}`)

    return team.value
  }

  console.warn(
    `Team code '${teamCode}' not found in available teams. Using default.`,
  )

  return teamOptions.value.length > 0 ? teamOptions.value[0].value : null
}

const mapFileDataRowToForm = row => {
  console.group(" -- MapFileDataRowToForm invoked --")
  console.log("Input row =>", JSON.parse(JSON.stringify(row)))

  let imagesArray = []

  // Image fields recovery
  const rawImages =
    row.images || row.N || row["Images"] || row["image"] || null

  if (rawImages && Array.isArray(rawImages)) {
    console.group(
      ` Image mapping (${rawImages.length} image${
        rawImages.length > 1 ? "s" : ""
      })`,
    )

    rawImages.forEach((img, index) => {
      if (typeof img === "string") {
        console.log(`image[${index}] string →`, img)
      } else if (typeof img === "object" && img.url) {
        console.log(`image[${index}] object.url →`, img.url)
      } else {
        console.warn(`image[${index}]  Ignored (unknown type):`, img)
      }
    })

    imagesArray = rawImages
      .map(img => {
        if (typeof img === "object" && img.url) return img.url
        if (typeof img === "string") return img

        return null
      })
      .filter(Boolean)

    console.groupEnd()
  } else {
    console.warn("No image arrays detected in row.images or row.N")
  }

  // Map team code from the serial-number column to team_id. Prefer the
  // normalized key emitted by the parse-only header detector.
  const teamCode = row._serial_number || row.A || row.team_code || null
  const teamId = getTeamIdFromCode(teamCode)

  console.log(`Row team code: '${teamCode}' → team_id: ${teamId}`)

  const mappedForm = {
    date: row.date || "",
    barcode: row._barcode || row.G || row.barcode || "",
    bag_code: row._bag_code || row.F || row.bag_code || "",
    status: "received",
    transport: 1,
    length: parseFloat(row._length || row.K || row.length) || 0,
    width: parseFloat(row._width || row.L || row.width) || 0,
    height: parseFloat(row._height || row.M || row.height) || 0,
    weight: parseFloat(row._weight || row.J || row.weight) || 0,
    volume: calculateVolume(
      row._length || row.K || row.length,
      row._width || row.L || row.width,
      row._height || row.M || row.height,
    ),
    team_id: teamId,
    customer: row.customer || null,
    notes: row.notes || "",
    product_lists: row._product_name || row.H || row.product_name || "",
    product_lines: row.product_lines || [],
    images: imagesArray,
  }

  console.group("Mapped images output")
  console.table(imagesArray)
  console.groupEnd()

  console.groupEnd()

  return mappedForm
}

const submitForm = async () => {
  if (!form.value) return false

  try {
    console.log("-- SubmitForm Invoked --")

    const formData = new FormData()

    // Image management
    const localFiles = []
    const existingImages = []

    form.value.images?.forEach(img => {
      if (img instanceof File) {
        localFiles.push(img)
      } else if (typeof img === "string") {
        existingImages.push(img)
      }
    })

    // Add local files
    localFiles.forEach(file => formData.append("images[]", file))

    // Add existing image in JSON
    if (existingImages.length) {
      formData.append("existing_images", JSON.stringify(existingImages))
    }

    const keys = [
      "barcode",
      "bag_code",
      "status",
      "sale_commission_agent",
      "transport",
      "length",
      "width",
      "height",
      "weight",
      "notes",
      "parcel_type",
      "team_id",
      "customer_id",
      "product_lists", // Add product_lists to submitted fields
    ]

    keys.forEach(key => {
      if (form.value[key] !== undefined && form.value[key] !== null) {
        formData.append(key, form.value[key].toString())
      }
    })

    // Product lines
    form.value.product_lines?.forEach((line, index) => {
      formData.append(
        `shipment_product_lines[${index}][shipment_product_id]`,
        line.shipment_product_id,
      )
      formData.append(
        `shipment_product_lines[${index}][quantity]`,
        line.quantity,
      )
      if (line.detail)
        formData.append(
          `shipment_product_lines[${index}][detail]`,
          line.detail,
        )
    })

    console.group("Outgoing request payload")
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(
          `${key}: FILE - name=${value.name}, size=${value.size}, type=${value.type}`,
        )
      } else {
        console.log(`${key}:`, value)
      }
    }
    console.groupEnd()

    const response = await axios.post("/shipment/incoming-parcels", formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        "Content-Type": "multipart/form-data",
        Accept: "application/json",
      },
      timeout: 300000,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    })

    console.log("submit response ##==>", response.data)

    return response.data
  } catch (err) {
    console.error("Form submission error:", err.response?.data || err)

    return false
  }
}

// Cancel bulk import
const cancelBulkImport = () => {
  bulkImportProgress.cancelled = true
}

// Reset progress tracking
const resetBulkImportProgress = () => {
  Object.assign(bulkImportProgress, {
    current: 0,
    total: 0,
    percentage: 0,
    successCount: 0,
    errorCount: 0,
    currentItem: "",
    errors: [],
    cancelled: false,
  })
}

const createParcelsFromFileData = async () => {
  console.log("fileData : ", fileData.value)
  if (!fileData.value || fileData.value.length === 0) {
    console.warn("No files imported")
    
    return
  }

  // Reset progress
  resetBulkImportProgress()

  isLoading.value = true
  showSuccessAlert.value = false
  console.log("⚡ Starting OPTIMIZED bulk parcel creation...")

  // Set flag to disable WebSocket notifications during bulk import
  window.__bulkImportInProgress = true

  bulkImportProgress.total = fileData.value.length
  bulkImportProgress.currentItem = "Preparing parcels for bulk creation..."

  try {
    // ⚡ PERFORMANCE: Prepare all parcels data at once.
    // Use ONLY normalized keys (_barcode, _bag_code, ...) emitted by the
    // header-aware parse-only job. Don't fall back to raw column letters
    // — different China templates put different fields in column G/H/I/...
    // so a legacy fallback can dump quantity into barcode etc.
    const parcelsData = fileData.value.map(row => {
      const length = parseFloat(row._length) || 0
      const width = parseFloat(row._width) || 0
      const height = parseFloat(row._height) || 0
      const quantity = parseInt(row._quantity) || 1

      return {
        serial_number: row._serial_number || "",
        barcode: row._barcode || "",
        bag_code: row._bag_code || "",
        customer_name: row._customer_name || "",
        customer_phone: row._customer_phone || "",
        weight: parseFloat(row._weight) || 0,
        length: length,
        width: width,
        height: height,
        quantity: quantity,
        product_name: row._product_name || "",
        transport: 1, // Default to ship
      }
    })

    console.log(`⚡ Prepared ${parcelsData.length} parcels for bulk creation`)
    bulkImportProgress.currentItem = `Sending ${parcelsData.length} parcels to server...`

    // ⚡ PERFORMANCE: Send all parcels in ONE API call
    const response = await axios.post(
      "/shipment/incoming-parcels/bulk-store",
      { parcels: parcelsData },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
        timeout: 300000, // 5 minutes for large batches
      },
    )

    console.log("✅ Bulk creation response:", response.data)

    let data = response.data
    try {
      if (typeof data === "string") {
        // Remove trailing garbage
        const clean = data.trim().replace(/\}\{/g, "}")

        data = JSON.parse(clean)
      }
    } catch (e) {
      console.error("⚠️ JSON parse failed:", e, "Response:", response.data)
    }

    if (data.success) {
      const results = response.data.data

      bulkImportProgress.successCount = results.created
      bulkImportProgress.errorCount = results.errors?.length || 0
      bulkImportProgress.current = results.total
      bulkImportProgress.percentage = 100

      const skipped = results.skipped || 0
      const silentSkipped = results.silent_skipped || 0
      const parked = results.parked || 0

      // Show summary notification
      if (results.created > 0 && bulkImportProgress.errorCount === 0) {
        let msg = `✅ ${results.created} parcel${
          results.created > 1 ? "s" : ""
        } created`
        if (skipped > 0) msg += `, ${skipped} duplicate${skipped > 1 ? "s" : ""} skipped`
        if (parked > 0) msg += `, ${parked} parked`
        success(msg)
      } else if (results.created > 0 && bulkImportProgress.errorCount > 0) {
        error(
          `⚠️ ${results.created} parcels created, ${bulkImportProgress.errorCount} failed`,
        )
      } else if (bulkImportProgress.errorCount === 0 && (skipped > 0 || silentSkipped > 0 || parked > 0)) {
        // No new parcels, but no errors either — daily-cumulative re-import.
        let msg = `ℹ️ Nothing new to create`
        const parts = []
        if (skipped > 0) parts.push(`${skipped} already imported`)
        if (silentSkipped > 0) parts.push(`${silentSkipped} ignored`)
        if (parked > 0) parts.push(`${parked} parked`)
        if (parts.length) msg += ` (${parts.join(", ")})`
        success(msg)
      } else {
        error(`❌ Failed to create parcels`)
      }

      // Refresh parcel list
      await fetchParcels()

      // Close the dialog after short delay
      setTimeout(() => {
        importedFileDialog.value = false
      }, 1500)
    } else {
      throw new Error(response.data.message || "Bulk creation failed")
    }
  } catch (err) {
    console.error("❌ Error in bulk parcel creation:", err)
    bulkImportProgress.errorCount = bulkImportProgress.total
    error(
      `Failed to create parcels: ${err.response?.data?.message || err.message}`,
    )
  } finally {
    // Re-enable WebSocket notifications
    window.__bulkImportInProgress = false
    isLoading.value = false
    bulkImportProgress.currentItem = "Completed"
    console.log("⚡ Bulk import completed")
  }
}
</script>

<template>
  <div>
    <!-- Admin Access Indicator -->
    <VAlert
      v-if="isAdmin"
      type="success"
      variant="tonal"
      class="mb-6"
    >
      <VAlertTitle>
        {{ t("shipment.admin_access", "Administrator Access") }}
      </VAlertTitle>
      <p class="mb-0">
        {{
          t(
            "shipment.admin_access_description",
            "You have full access to all teams and parcels."
          )
        }}
      </p>
    </VAlert>

    <!-- Authorized User Access Indicator -->
    <VAlert
      v-else
      type="info"
      variant="tonal"
      class="mb-6"
    >
      <VAlertTitle>
        {{ t("shipment.parcel_access", "Parcel Access") }}
      </VAlertTitle>
      <p class="mb-0">
        {{
          t(
            "shipment.parcel_access_description",
            "You have access to view and manage incoming parcels."
          )
        }}
      </p>
    </VAlert>

    <!-- Main Content -->
    <div>
      <!-- Filters -->
      <VCard class="mb-6">
        <VCardText>
          <VRow>
            <!-- Team Filter -->
            <VCol
              cols="12"
              md="3"
            >
              <VSelect
                v-model="filters.team_id"
                :items="teamOptions"
                item-title="title"
                item-value="value"
                :label="t('shipment.team')"
                density="comfortable"
                variant="outlined"
                clearable
              >
                <template #selection="{ item }">
                  <VChip
                    :color="getTeamBadgeColor(item.raw.code)"
                    size="small"
                  >
                    {{ item.title }}
                  </VChip>
                </template>

                <template #item="{ item, props }">
                  <VListItem v-bind="props">
                    <template #prepend>
                      <VChip
                        :color="getTeamBadgeColor(item.raw.code)"
                        size="small"
                      >
                        {{ item.raw.code }}
                      </VChip>
                    </template>
                  </VListItem>
                </template>
              </VSelect>
            </VCol>

            <!-- Status Filter -->
            <VCol
              cols="12"
              md="3"
            >
              <VSelect
                v-model="filters.status"
                :items="statusOptions"
                item-title="title"
                item-value="value"
                :label="t('shipment.status')"
                clearable
              />
            </VCol>

            <!-- Month Filter -->
            <VCol
              cols="12"
              md="3"
            >
              <VSelect
                v-model="filters.month"
                :items="monthOptions"
                item-title="title"
                item-value="value"
                :label="t('common.month', 'Month')"
                clearable
              />
            </VCol>

            <!-- Transport Filter -->
            <VCol
              cols="12"
              md="3"
            >
              <VSelect
                v-model="filters.transport"
                :items="[
                  { title: t('common.all'), value: 'all' },
                  { title: t('shipment.by_plane'), value: 0 },
                  { title: t('shipment.by_ship'), value: 1 },
                ]"
                :label="t('shipment.transport')"
              />
            </VCol>

            <!-- Search -->
            <VCol
              cols="12"
              md="3"
            >
              <VTextField
                v-model="filters.search"
                :label="t('common.search')"
                prepend-inner-icon="tabler-search"
                clearable
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <!-- Action Buttons -->
      <VCard class="mb-6">
        <VCardText>
          <div class="d-flex flex-wrap gap-3">
            <!-- Create Parcel -->
            <VBtn
              v-if="canCreateParcel"
              color="primary"
              @click="router.push('/shipment/incoming-parcels/create')"
            >
              <VIcon
                start
                icon="tabler-plus"
              />
              {{ t("shipment.create_parcel") }}
            </VBtn>

            <!-- Upload file -->
            <VBtn
              v-if="canCreateParcel"
              prepend-icon="tabler-file-upload"
              color="success"
              class="ms-2"
              @click="importDialog = true"
            >
              {{ t("shipment.import_excel") || "Import Excel" }}
            </VBtn>

            <!-- Single Image Scan & Find Parcel -->
            <VBtn
              v-if="canCreateParcel"
              color="secondary"
              class="ms-2"
              @click="showSingleImageScanDialog = true"
            >
              <VIcon
                start
                icon="tabler-scan"
              />
              {{ t("shipment.scan_find_parcel") || "Scan & Find Parcel" }}
            </VBtn>

            <!-- Show File -->
            <!--
              <VBtn
              v-if="showFileButton"
              color="warning"
              class="ms-2"
              @click="openImportedFileDialog()"
              >
              <VIcon start icon="tabler-eye" />
              Imported file
              </VBtn> 
            -->

            <!-- Bulk Update -->
            <VBtn
              v-if="canBulkUpdate && selectedRows.length > 0"
              color="info"
              @click="openBulkEditDialog"
            >
              <VIcon
                start
                icon="tabler-edit"
              />
              {{ t("common.bulk_update") }} ({{ selectedRows.length }})
            </VBtn>

            <!--
              Bulk Claim — attach all selected parcels to one customer.
              Backend (POST /shipment/claims/bulk-attach) is role-gated
              to admin/superadmin/team-manager + skips parcels that
              already have a customer. 
            -->
            <VBtn
              v-if="selectedRows.length > 0"
              color="primary"
              variant="elevated"
              @click="openBulkClaimDialog"
            >
              <VIcon
                start
                icon="tabler-link"
              />
              {{ t("claim_dialog.bulk_action") }} ({{ selectedRows.length }})
            </VBtn>

            <!-- Mass Delete -->
            <VBtn
              v-if="isAdmin && selectedRows.length > 0"
              color="error"
              @click="handleMassDelete"
            >
              <VIcon
                start
                icon="tabler-trash"
              />
              {{ t("common.mass_delete") }} ({{ selectedRows.length }})
            </VBtn>

            <!-- Create Package -->
            <VBtn
              v-if="isAdmin && selectedRows.length > 0"
              color="success"
              @click="createPackageFromSelected"
            >
              <VIcon
                start
                icon="tabler-package"
              />
              {{ t("shipment.create_package") }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>

      <!-- Data Table -->
      <VCard>
        <VDataTableServer
          :key="tableKey"
          v-model="selectedRows"
          v-model:sort-by="tableSort"
          :headers="tableHeaders"
          :items="parcels"
          :loading="loading"
          :items-length="parcelsMeta.total || 0"
          :page="currentPage"
          :items-per-page="itemsPerPage"
          item-value="id"
          show-select
          @update:options="handleTableOptionsUpdate"
        >
          <!-- Image Column -->
          <template #item.image="{ item }">
            <div class="d-flex justify-center">
              <div
                class="image-cell cursor-pointer"
                @click="handleImageClick(item)"
              >
                <!-- Show parcel image if available, otherwise show default -->
                <img
                  v-if="item.images && item.images.length > 0"
                  :src="
                    item.images[0].url || item.image_url || getImageUrl(item)
                  "
                  alt="Parcel image"
                  width="40"
                  height="40"
                  class="rounded"
                  @error="(e) => (e.target.src = '/img/default.png')"
                >
                <div
                  v-else
                  class="d-flex align-center justify-center"
                >
                  <img
                    src="/img/default.png"
                    alt="Upload image"
                    width="40"
                    height="40"
                    class="rounded opacity-75"
                  >
                  <VIcon
                    class="upload-icon"
                    size="x-small"
                    color="primary"
                  >
                    tabler-upload
                  </VIcon>
                </div>
              </div>
            </div>
          </template>

          <!-- Date Column -->
          <template #item.date="{ item }">
            <span>{{ formatDate(item.date || item.created_at) }}</span>
          </template>

          <!-- Bag Code Column -->
          <template #item.bag_code="{ item }">
            <span>{{ item.bag_code || "-" }}</span>
          </template>

          <!-- Team Column -->
          <template #item.team="{ item }">
            <VChip
              v-if="item.team?.name"
              :color="getTeamBadgeColor(item.team.code)"
              size="small"
            >
              {{ item.team.name }} ({{ item.team.code }})
            </VChip>
            <span
              v-else
              class="text-disabled"
            >-</span>
          </template>

          <!-- Customer Column -->
          <template #item.customer="{ item }">
            <div class="customer-info">
              <CustomerLink
                :customer="item.customer"
                :name="item.customer?.display"
                :show-client-id="false"
              />
              <div
                v-if="item.customer?.phone"
                class="customer-phone text-caption text-grey-darken-1"
              >
                {{ item.customer.phone }}
              </div>
            </div>
          </template>

          <!-- Products Column -->
          <template #item.products="{ item }">
            <div class="products-cell">
              {{ item.products_display_text || "-" }}
            </div>
          </template>

          <!-- Notes Column -->
          <template #item.notes="{ item }">
            <div class="notes-cell">
              <span
                v-if="item.notes"
                class="notes-content"
              >{{
                item.notes
              }}</span>
              <span
                v-else
                class="text-grey-lighten-1"
              >-</span>
            </div>
          </template>

          <!-- Weight Column -->
          <template #item.weight="{ item }">
            <span
              v-if="item.weight"
              class="font-weight-medium"
            >
              {{ Number(item.weight).toFixed(2) }} kg
            </span>
            <span
              v-else
              class="text-grey-lighten-1"
            >-</span>
          </template>

          <!-- Volume Column -->
          <template #item.volume="{ item }">
            <span
              v-if="item.volume && Number(item.volume) > 0"
              class="font-weight-medium"
            >
              {{ Number(item.volume).toFixed(3) }} m³
            </span>
            <span
              v-else
              class="text-grey-lighten-1"
            >-</span>
          </template>

          <!-- Transport Column -->
          <template #item.transport="{ item }">
            <VChip
              :color="item.transport === 0 ? 'info' : 'primary'"
              size="small"
            >
              {{
                item.transport === 0
                  ? t("shipment.by_plane")
                  : item.transport === 1
                    ? t("shipment.by_ship")
                    : item.transport
              }}
            </VChip>
          </template>

          <!-- Status Column -->
          <template #item.status="{ item }">
            <VChip
              :color="item.status_color || getStatusColor(item.status, item)"
              size="small"
            >
              <VIcon
                v-if="item.status_icon"
                :icon="item.status_icon"
                size="x-small"
                class="me-1"
              />
              {{
                t(
                  `shipment.statuses.${item.status_label || item.status_key}`,
                  item.status_label
                )
              }}
            </VChip>
          </template>

          <!-- Actions Column -->
          <template #item.actions="{ item }">
            <div class="d-flex gap-1">
              <!-- View → full parcel detail page (was the lightweight modal). -->
              <VBtn
                v-if="canAccessParcel(item)"
                icon
                size="small"
                color="info"
                variant="text"
                :to="{ name: 'shipment-incoming-parcels-id', params: { id: item.id } }"
              >
                <VIcon icon="tabler-eye" />
                <VTooltip activator="parent">
                  {{ t("common.view") }}
                </VTooltip>
              </VBtn>

              <!--
                Claim / Reassign — single-parcel attach when unattached,
                reassign dialog when a customer is already set. Visible
                to anyone with parcel-read; server gates the write to
                role:admin|superadmin|team-manager. 
              -->
              <VBtn
                v-if="canAccessParcel(item)"
                icon
                size="small"
                :color="item.customer ? 'warning' : 'primary'"
                variant="text"
                @click="openClaimDialog(item)"
              >
                <VIcon :icon="item.customer ? 'tabler-arrow-right' : 'tabler-link'" />
                <VTooltip activator="parent">
                  {{ item.customer ? t("claim_dialog.reassign_action") : t("claim_dialog.claim_action") }}
                </VTooltip>
              </VBtn>

              <!-- Edit -->
              <VBtn
                v-if="canEditSpecificParcel(item)"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="editParcel(item)"
              >
                <VIcon icon="tabler-edit" />
                <VTooltip activator="parent">
                  {{ t("common.edit") }}
                </VTooltip>
              </VBtn>

              <!-- Delete -->
              <VBtn
                v-if="canDeleteSpecificParcel(item)"
                icon
                size="small"
                color="error"
                variant="text"
              >
                <VIcon icon="tabler-trash" />
                <VTooltip activator="parent">
                  {{ t("common.delete") }}
                </VTooltip>
              </VBtn>
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </div>

    <!-- Imported File Dialog -->
    <VDialog
      v-model="importedFileDialog"
      max-width="900px"
      persistent
    >
      <VCard class="px-5">
        <VCardTitle> Imported Files </VCardTitle>

        <!-- Progress Section -->
        <VCardText v-if="isLoading">
          <div class="mb-4">
            <div class="d-flex justify-space-between mb-2">
              <span class="text-body-1 font-weight-medium">
                {{ bulkImportProgress.currentItem }}
              </span>
              <span class="text-body-2">
                {{ bulkImportProgress.current }} /
                {{ bulkImportProgress.total }}
              </span>
            </div>

            <VProgressLinear
              :model-value="bulkImportProgress.percentage"
              height="25"
              color="primary"
              striped
              rounded
            >
              <template #default>
                <strong>{{ bulkImportProgress.percentage }}%</strong>
              </template>
            </VProgressLinear>

            <div class="d-flex justify-space-between mt-2">
              <VChip
                size="small"
                color="success"
                variant="flat"
              >
                <VIcon
                  start
                  icon="tabler-check"
                />
                Success: {{ bulkImportProgress.successCount }}
              </VChip>
              <VChip
                v-if="bulkImportProgress.errorCount > 0"
                size="small"
                color="error"
                variant="flat"
              >
                <VIcon
                  start
                  icon="tabler-x"
                />
                Failed: {{ bulkImportProgress.errorCount }}
              </VChip>
            </div>
          </div>
        </VCardText>

        <!-- Errors Display -->
        <VCardText v-if="bulkImportProgress.errors.length > 0 && !isLoading">
          <VAlert
            type="error"
            variant="tonal"
            class="mb-4"
          >
            <VAlertTitle>
              Import Errors ({{
                bulkImportProgress.errors.length
              }})
            </VAlertTitle>
            <VList density="compact">
              <VListItem
                v-for="(err, index) in bulkImportProgress.errors.slice(0, 5)"
                :key="index"
              >
                <VListItemTitle>
                  <strong>Row {{ err.row }}:</strong> {{ err.barcode }}
                </VListItemTitle>
                <VListItemSubtitle class="text-error">
                  {{ err.error }}
                </VListItemSubtitle>
              </VListItem>
              <VListItem v-if="bulkImportProgress.errors.length > 5">
                <VListItemTitle class="text-center text-caption">
                  ... and {{ bulkImportProgress.errors.length - 5 }} more errors
                </VListItemTitle>
              </VListItem>
            </VList>
          </VAlert>
        </VCardText>

        <!-- Action Buttons -->
        <div class="d-flex justify-end gap-3 px-5 mb-4">
          <VBtn
            v-if="isLoading"
            color="error"
            variant="outlined"
            @click="cancelBulkImport"
          >
            <VIcon
              start
              icon="tabler-x"
            />
            Cancel Import
          </VBtn>
          <VBtn
            v-else
            color="primary"
            class="w-auto"
            :disabled="isLoading"
            @click="createParcelsFromFileData"
          >
            <VIcon
              start
              icon="tabler-package"
            />
            Create Parcels
          </VBtn>
          <VBtn
            color="secondary"
            class="w-auto"
            variant="tonal"
            :disabled="isLoading"
            @click="importedFileDialog = false"
          >
            {{ isLoading ? "Processing..." : "Close" }}
          </VBtn>
        </div>

        <!-- Data Table -->
        <VDataTable
          :headers="fileTableHeaders"
          :items="fileData"
          :loading="isFileLoading"
          class="elevation-1 mt-4 mb-5"
          :items-per-page="10"
          density="compact"
        />
      </VCard>
    </VDialog>

    <!-- Import Excel Dialog -->
    <VDialog
      v-model="importDialog"
      max-width="600px"
      persistent
    >
      <VCard>
        <VCardTitle>
          {{
            t("shipment.import_incoming_parcels_list") ||
              "Import Incoming Parcel"
          }}
        </VCardTitle>

        <VCardText>
          <VAlert
            v-if="importErrorMessage"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ importErrorMessage }}
          </VAlert>

          <VAlert
            v-if="importSuccessMessage"
            type="success"
            variant="tonal"
            class="mb-4"
          >
            {{ importSuccessMessage }}
          </VAlert>

          <VProgressLinear
            v-if="importLoading"
            class="mb-4"
            color="primary"
            :model-value="importProgress"
            height="20"
            rounded
            striped
          >
            <template #default>
              {{ importProgressMessage || `Processing (${importProgress}%)` }}
            </template>
          </VProgressLinear>

          <VForm @submit.prevent="uploadExcelFile">
            <VRow>
              <VCol cols="12">
                <VFileInput
                  v-model="importForm.excel_file"
                  :label="t('shipment.excel_file') || 'Excel File'"
                  accept=".xlsx,.xls"
                  :disabled="importLoading"
                  :rules="[(v) => !!v || 'Excel file is required']"
                  :placeholder="
                    t('shipment.excel_placeholder') ||
                      'Click to select or drop Excel file'
                  "
                  :hint="
                    t('shipment.excel_hint') || 'Supported formats: .xlsx, .xls'
                  "
                  persistent-hint
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            color="secondary"
            variant="outlined"
            :disabled="importLoading"
            @click="importDialog = false"
          >
            {{ t("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="importLoading"
            :disabled="importLoading"
            @click="uploadExcelFile"
          >
            {{ t("actions.upload") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Parcel View Modal -->
    <ParcelViewModal
      v-model="viewDialogOpen"
      :parcel="selectedParcelForView"
      :can-delete="canDeleteParcel"
      :can-edit="canEditParcel"
      @parcel-updated="handleParcelUpdated"
    />

    <!-- Claim / Reassign / Bulk-claim dialog -->
    <ClaimParcelDialog
      v-model="claimDialogOpen"
      :mode="claimDialogMode"
      :parcel-id="claimDialogParcel?.id"
      :barcode="claimDialogParcel?.barcode"
      :current-customer-id="claimDialogParcel?.customer?.id"
      :current-customer-name="claimDialogParcel?.customer?.name"
      :parcel-ids="claimDialogParcelIds"
      @done="onClaimDone"
    />

    <!-- Single Image Barcode Scan Dialog -->
    <SingleImageBarcodeUpload v-model="showSingleImageScanDialog" />

    <!-- Confirm Delete Dialog -->
    <VDialog
      v-model="showConfirmDeleteDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle class="text-h5">
          {{ t("messages.confirmDeleteTitle") }}
        </VCardTitle>
        <VCardText>
          {{
            t("messages.confirmDeleteMultiple", { count: selectedRows.length })
          }}
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="secondary"
            variant="text"
            @click="showConfirmDeleteDialog = false"
          >
            {{ t("actions.cancel") }}
          </VBtn>
          <VBtn
            color="error"
            @click="handleDeleteSelected"
          >
            {{ t("actions.delete") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Package Type Selection Dialog -->
    <VDialog
      v-model="showPackageTypeDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle class="text-h5">
          {{ t("shipment.createPackageConfirmation", "Create Package") }}
        </VCardTitle>
        <VCardText>
          <p class="mb-4">
            {{
              t(
                "shipment.createPackageOptions",
                "How would you like to create packages from the selected parcels?"
              )
            }}
          </p>
          <VRadioGroup v-model="packageMode">
            <VRadio
              value="individual"
              :label="
                t('shipment.createIndividualPackages', 'Individual Packages')
              "
            />
            <VRadio
              value="single"
              :label="t('shipment.createOnePackage', 'One Package')"
            />
          </VRadioGroup>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="secondary"
            variant="text"
            @click="showPackageTypeDialog = false"
          >
            {{ t("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            @click="handlePackageTypeSelection(packageMode)"
          >
            {{ t("actions.create") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Image Upload Dialog -->
    <VDialog
      v-model="showUploadImagesDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle class="text-h5">
          {{ t("shipment.uploadImages") }}
          <span
            v-if="selectedParcelForView"
            class="text-subtitle-1 ml-2"
          >
            ({{ t("shipment.parcel") }} #{{ selectedParcelForView.id || "" }})
          </span>
        </VCardTitle>
        <VCardText>
          <p class="mb-4">
            {{ t("shipment.uploadImagesDescription") }}
          </p>

          <VAlert
            v-if="!selectedParcelForView"
            type="warning"
            dense
          >
            {{
              t("messages.selectParcelFirst", "Please select a parcel first")
            }}
          </VAlert>

          <div v-else>
            <VFileInput
              v-model="uploadedImages"
              :label="t('shipment.selectImages')"
              accept="image/*"
              multiple
              prepend-icon="tabler-camera"
              :hint="
                t('shipment.imagesHint', 'Select up to 5 images (max 2MB each)')
              "
              persistent-hint
              :disabled="!selectedParcelForView"
              @change="handleImageUpload"
            />

            <!-- Preview uploaded images -->
            <div
              v-if="uploadedImages.length"
              class="d-flex flex-wrap gap-2 mt-4"
            >
              <div
                v-for="(image, index) in uploadedImages"
                :key="index"
                class="image-preview"
              >
                <img
                  :src="createImageURL(image)"
                  width="80"
                  height="80"
                  class="rounded"
                >
              </div>
            </div>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="secondary"
            variant="text"
            @click="showUploadImagesDialog = false"
          >
            {{ t("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :disabled="!selectedParcelForView || !uploadedImages.length"
            :loading="uploadingImages"
            @click="uploadImages"
          >
            {{ t("actions.upload") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Enhanced Bulk Edit Dialog -->
    <VDialog
      v-model="showBulkEditDialog"
      max-width="800"
      persistent
      scrollable
    >
      <VCard>
        <VCardTitle class="text-h5 d-flex align-center">
          <VIcon
            icon="tabler-edit"
            class="me-2"
          />
          {{ t("shipment.bulk_update_parcels", "Bulk Update Parcels") }}
        </VCardTitle>

        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
          >
            {{
              t(
                "shipment.bulk_edit_description",
                "Update multiple parcels at once. Only filled fields will be updated."
              )
            }}
            <br>
            <strong>{{
              t("shipment.selectedItems", { count: selectedRows.length }) ||
                `Selected Items: ${selectedRows.length}`
            }}</strong>
            <br>
            <span
              v-if="selectedRows.length > 0"
              class="text-sm text-medium-emphasis"
            >
              {{ t("shipment.selectedParcels", "Selected Parcels") }}:
              {{
                selectedRows.map((row) => getParcelDisplayId(row)).join(", ")
              }}
            </span>
          </VAlert>

          <VTabs
            v-model="bulkEditActiveTab"
            class="mb-4"
          >
            <VTab>{{ t("shipment.basicInfo") }}</VTab>
            <VTab>{{ t("shipment.customerInfo", "Customer Info") }}</VTab>
            <VTab>{{ t("shipment.products") }}</VTab>
          </VTabs>

          <VWindow v-model="bulkEditActiveTab">
            <!-- Basic Info Tab -->
            <VWindowItem value="0">
              <VForm>
                <VRow class="mt-1">
                  <!-- Parcel Type -->
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VSelect
                      v-model="bulkEditForm.parcel_type"
                      :items="[
                        { title: 'Standard', value: 'standard' },
                        { title: 'Express', value: 'express' },
                        { title: 'Fragile', value: 'fragile' },
                        { title: 'Electronics', value: 'electronics' },
                      ]"
                      :label="t('shipment.parcelType')"
                      clearable
                      density="comfortable"
                      variant="outlined"
                    />
                  </VCol>

                  <!-- Transport -->
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VSelect
                      v-model="bulkEditForm.transport"
                      :items="[
                        { title: t('shipment.by_plane'), value: 0 },
                        { title: t('shipment.by_ship'), value: 1 },
                      ]"
                      :label="t('shipment.transport')"
                      clearable
                      density="comfortable"
                      variant="outlined"
                    />
                  </VCol>

                  <!-- Team -->
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VSelect
                      v-model="bulkEditForm.team_id"
                      :items="teamOptions"
                      item-title="title"
                      item-value="value"
                      :label="t('shipment.team')"
                      clearable
                      density="comfortable"
                      variant="outlined"
                    />
                  </VCol>

                  <!-- Bag Code -->
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextField
                      v-model="bulkEditForm.bag_code"
                      :label="t('shipment.bagCode')"
                      density="comfortable"
                      variant="outlined"
                      clearable
                    />
                  </VCol>

                  <!-- Dimension Fields Section -->
                  <VCol cols="12">
                    <VDivider class="my-2" />
                    <div class="text-subtitle-2 mb-3 text-medium-emphasis">
                      {{ t("shipment.dimensions", "Dimensions (Optional)") }}
                    </div>
                  </VCol>

                  <!-- Length -->
                  <VCol
                    cols="12"
                    md="3"
                  >
                    <VTextField
                      v-model.number="bulkEditForm.length"
                      :label="t('shipment.length')"
                      type="number"
                      min="0"
                      step="0.01"
                      density="comfortable"
                      variant="outlined"
                      clearable
                      suffix="cm"
                    />
                  </VCol>

                  <!-- Width -->
                  <VCol
                    cols="12"
                    md="3"
                  >
                    <VTextField
                      v-model.number="bulkEditForm.width"
                      :label="t('shipment.width')"
                      type="number"
                      min="0"
                      step="0.01"
                      density="comfortable"
                      variant="outlined"
                      clearable
                      suffix="cm"
                    />
                  </VCol>

                  <!-- Height -->
                  <VCol
                    cols="12"
                    md="3"
                  >
                    <VTextField
                      v-model.number="bulkEditForm.height"
                      :label="t('shipment.height')"
                      type="number"
                      min="0"
                      step="0.01"
                      density="comfortable"
                      variant="outlined"
                      clearable
                      suffix="cm"
                    />
                  </VCol>

                  <!-- Weight -->
                  <VCol
                    cols="12"
                    md="3"
                  >
                    <VTextField
                      v-model.number="bulkEditForm.weight"
                      :label="t('shipment.weight')"
                      type="number"
                      min="0"
                      step="0.01"
                      density="comfortable"
                      variant="outlined"
                      clearable
                      suffix="kg"
                    />
                  </VCol>

                  <!-- Volume -->
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextField
                      v-model.number="bulkEditForm.volume"
                      :label="t('shipment.volume')"
                      type="number"
                      min="0"
                      step="0.001"
                      density="comfortable"
                      variant="outlined"
                      clearable
                      suffix="m³"
                    />
                  </VCol>

                  <!-- Notes -->
                  <VCol cols="12">
                    <VTextarea
                      v-model="bulkEditForm.notes"
                      :label="t('shipment.notes')"
                      rows="3"
                      density="comfortable"
                      variant="outlined"
                    />
                  </VCol>
                </VRow>
              </VForm>
            </VWindowItem>

            <!-- Customer Info Tab -->
            <VWindowItem value="1">
              <VForm>
                <VRow>
                  <!-- Customer Selection with Create New Option -->
                  <VCol cols="12">
                    <VLabel class="mb-2">
                      {{ t("shipment.select_customer", "Select Customer") }}
                    </VLabel>
                    <CustomerSelector
                      v-model="bulkEditCustomerSelection"
                      @create-new="handleBulkCustomerCreateNew"
                      @update:model-value="handleBulkCustomerSelect"
                    />
                  </VCol>
                </VRow>
              </VForm>
            </VWindowItem>

            <!-- Products Tab -->
            <VWindowItem value="2">
              <!-- Product Manager Component -->
              <ProductManager
                v-model="bulkEditForm.products"
                @create-new="handleCreateNewProduct"
              />
            </VWindowItem>
          </VWindow>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            color="secondary"
            variant="text"
            :disabled="bulkEditLoading"
            @click="closeBulkEditDialog"
          >
            {{ t("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="bulkEditLoading"
            @click="submitBulkEdit"
          >
            {{
              t("shipment.update_selected_parcels", "Update Selected Parcels")
            }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Customer Creation Modal for Bulk Edit -->
    <ContactForm
      v-model="showBulkCustomerModal"
      :initial-name="newBulkCustomerName"
      @created="handleBulkCustomerCreated"
    />
  </div>
</template>

<style scoped>
.v-table {
  width: 100%;
}

.image-cell {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

.image-cell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-icon {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.products-cell {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notes-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notes-content {
  font-size: 0.875rem;
  line-height: 1.2;
  color: rgba(var(--v-theme-on-surface), 0.8);
}

.image-preview {
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.image-preview:hover {
  transform: scale(1.05);
}

.customer-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.customer-name {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
}

.customer-phone {
  font-size: 0.75rem;
  line-height: 1;
  opacity: 0.8;
}
</style>
